/**
 * Refusing a privileged key in the browser.
 *
 * Supabase issues two kinds of key, and the difference is the difference
 * between "safe in a bundle" and "catastrophic in a bundle":
 *
 *   publishable / anon  — designed to be public. Row-level security decides
 *                         what its holder can actually see.
 *   secret / service_role — **bypasses row-level security entirely.**
 *
 * A build that shipped the second one would compile it into JavaScript that
 * anyone can read with View Source, and every policy in `auth-schema.sql` would
 * be worthless: any visitor could read and delete every family's data.
 *
 * The two are adjacent in the dashboard and the naming has changed over time
 * (`anon` became "publishable", `service_role` became "secret"), so this is an
 * easy mistake to make once and never notice. This module makes it impossible
 * to make quietly.
 */

export type KeyKind = 'publishable' | 'secret' | 'unknown';

/**
 * Classifies a key without needing the network.
 *
 * Handles both formats Supabase has used: the current `sb_publishable_…` /
 * `sb_secret_…` prefixes, and the older unprefixed JWTs whose payload carries
 * a `role` claim.
 */
export function classifyKey(key: string): KeyKind {
  const trimmed = key.trim();

  if (trimmed.startsWith('sb_secret_')) return 'secret';
  if (trimmed.startsWith('sb_publishable_')) return 'publishable';

  // Legacy keys are JWTs: header.payload.signature, base64url-encoded.
  const payload = decodeJwtPayload(trimmed);
  if (payload) {
    const role = typeof payload.role === 'string' ? payload.role : undefined;
    if (role === 'service_role') return 'secret';
    if (role === 'anon') return 'publishable';
  }

  return 'unknown';
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const parts = token.split('.');
  if (parts.length !== 3 || !parts[1]) return null;
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const json =
      typeof atob === 'function'
        ? atob(padded)
        : // Node, for the tests.
          Buffer.from(padded, 'base64').toString('utf8');
    const parsed: unknown = JSON.parse(json);
    return typeof parsed === 'object' && parsed !== null ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

export class PrivilegedKeyError extends Error {
  constructor() {
    super(
      'A Supabase SECRET key was found in VITE_SUPABASE_ANON_KEY. That key bypasses row-level ' +
        'security and would expose every account if it shipped in the browser. Use the ' +
        'publishable key instead — the one the dashboard describes as safe to use in a browser ' +
        '(labelled "Publishable key", or "anon public" on older projects). ' +
        'If this key has already been deployed, revoke it in Settings → API.',
    );
    this.name = 'PrivilegedKeyError';
  }
}

/**
 * Throws if the key must not be in a browser.
 *
 * Deliberately fails loudly rather than degrading: an unconfigured deployment
 * is a missing feature, but a leaked service key is an incident, and it should
 * be impossible to miss. An unrecognised key is allowed through — Supabase may
 * introduce new formats, and refusing to start on an unfamiliar prefix would be
 * worse than the risk it guards against.
 */
export function assertBrowserSafeKey(key: string): void {
  if (classifyKey(key) === 'secret') throw new PrivilegedKeyError();
}
